import React, { useEffect, useState } from 'react';
import {
  Button,
  Box,
  CircularProgress,
  Grid,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
  Paper,
  Card,
  CardContent,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Chip
} from '@mui/material';
import { LocalizationProvider, DatePicker } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import axios from 'axios';
import { API_BASE_URL } from '../../constants/constants';
import { DataGrid } from '@mui/x-data-grid';
import { format } from 'date-fns';
import BackButton from '../../components/BackButton';
import { useParams } from 'react-router-dom';

const VendorAcemoneySettlements = () => {
  const { shopId } = useParams();
  const [startDate, setStartDate] = useState(
    new Date(new Date().setDate(new Date().getDate() - 30))
  );
  const [endDate, setEndDate] = useState(new Date());
  const [openDialog, setOpenDialog] = useState(false);
  const [dialogMessage, setDialogMessage] = useState('');
  const [settlements, setSettlements] = useState([]);
  const [loading, setLoading] = useState(false);
  const [shop, setShop] = useState(null);
  const [statistics, setStatistics] = useState(null);
  const [pagination, setPagination] = useState({
    currentPage: 1,
    totalPages: 0,
    totalRecords: 0,
    limit: 25
  });

  // Filters
  const [status, setStatus] = useState('');

  // Settlement details dialog
  const [selectedSettlement, setSelectedSettlement] = useState(null);
  const [detailsDialogOpen, setDetailsDialogOpen] = useState(false);

  useEffect(() => {
    fetchSettlements();
  }, [pagination.currentPage, status]);

  const validateDates = () => {
    if (!startDate || !endDate) {
      setDialogMessage('Please select both start date and end date.');
      setOpenDialog(true);
      return false;
    }
    const diffInDays = Math.ceil((endDate - startDate) / (1000 * 60 * 60 * 24));
    if (startDate > endDate) {
      setDialogMessage('Start date must be on or before the end date.');
      setOpenDialog(true);
      return false;
    }
    if (diffInDays > 90) {
      setDialogMessage('Date range cannot be more than 90 days.');
      setOpenDialog(true);
      return false;
    }
    return true;
  };

  const fetchSettlements = async () => {
    if (!validateDates()) return;

    setLoading(true);
    try {
      const params = {
        page: pagination.currentPage,
        limit: pagination.limit,
        startDate: format(startDate, 'yyyy-MM-dd'),
        endDate: format(endDate, 'yyyy-MM-dd'),
        ...(status && { status })
      };

      const response = await axios.get(
        `${API_BASE_URL}/acemoney/reports/settlements/shop/${shopId}`,
        { params }
      );

      if (response.data.success) {
        setSettlements(response.data.settlements);
        setShop(response.data.shop);
        setStatistics(response.data.statistics);
        setPagination(response.data.pagination);
      }
    } catch (error) {
      console.error('Error fetching settlements:', error);
      setDialogMessage(
        'Failed to fetch settlements. Please try again.'
      );
      setOpenDialog(true);
    } finally {
      setLoading(false);
    }
  };

  const handlePageChange = (newPage) => {
    setPagination(prev => ({ ...prev, currentPage: newPage + 1 }));
  };

  const handleViewDetails = (settlement) => {
    setSelectedSettlement(settlement);
    setDetailsDialogOpen(true);
  };

  const getStatusColor = (status) => {
    switch (status?.toLowerCase()) {
      case 'approved':
        return 'success';
      case 'failed':
      case 'rejected':
        return 'error';
      case 'pending':
        return 'warning';
      default:
        return 'default';
    }
  };

  const columns = [
    {
      field: 'slNo',
      headerName: 'Sl No',
      width: 70,
      renderCell: (params) => params.api.getAllRowIds().indexOf(params.id) + 1 + (pagination.currentPage - 1) * pagination.limit
    },
    {
      field: 'settlementRefNo',
      headerName: 'Settlement Ref No',
      width: 200,
      renderCell: (params) => (
        <Typography
          variant="body2"
          sx={{ cursor: 'pointer', color: 'primary.main' }}
          onClick={() => handleViewDetails(params.row)}
        >
          {params.value}
        </Typography>
      )
    },
    {
      field: 'yppReferenceNumber',
      headerName: 'YPP Reference',
      width: 180
    },
    {
      field: 'settlementAmount',
      headerName: 'Settlement Amount',
      width: 150,
      renderCell: (params) => `₹${params.value?.toFixed(2)}`
    },
    {
      field: 'commission',
      headerName: 'Commission',
      width: 120,
      renderCell: (params) => `₹${params.value?.toFixed(2)}`
    },
    {
      field: 'registeredCount',
      headerName: 'Registered',
      width: 110,
      align: 'center'
    },
    {
      field: 'notRegisteredCount',
      headerName: 'Not Registered',
      width: 130,
      align: 'center'
    },
    {
      field: 'settlementStatus',
      headerName: 'Status',
      width: 120,
      renderCell: (params) => (
        <Chip
          label={params.value?.toUpperCase()}
          color={getStatusColor(params.value)}
          size="small"
        />
      )
    },
    {
      field: 'paymentMode',
      headerName: 'Settlement Mode', // [CASH-MODE] NEFT/IMPS, not UPI/Cash
      width: 130
    },
    {
      field: 'settlementDate',
      headerName: 'Settlement Date',
      width: 180,
      renderCell: (params) => {
        if (!params.value) return 'N/A';
        return format(new Date(params.value), 'dd MMM yyyy, hh:mm a');
      }
    }
  ];

  return (
    <Box sx={{ position: 'relative', padding: 3 }}>
      <BackButton />

      {/* Shop Info */}
      {shop && (
        <Paper sx={{ p: 2, mb: 3, mt: 2 }}>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={4}>
              <Typography variant="subtitle2" color="textSecondary">
                Shop Name
              </Typography>
              <Typography variant="h6">{shop.shopname}</Typography>
            </Grid>
            <Grid item xs={12} sm={4}>
              <Typography variant="subtitle2" color="textSecondary">
                Shop Code
              </Typography>
              <Typography variant="h6">{shop.shopCode}</Typography>
            </Grid>
            <Grid item xs={12} sm={4}>
              <Typography variant="subtitle2" color="textSecondary">
                Phone Number
              </Typography>
              <Typography variant="h6">{shop.phoneNumber}</Typography>
            </Grid>
          </Grid>
        </Paper>
      )}

      <Typography variant="h4" gutterBottom sx={{ mb: 3 }}>
        AceMoney Settlements
      </Typography>

      {/* Statistics Cards */}
      {statistics && (
        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent>
                <Typography color="textSecondary" gutterBottom>
                  Total Settlements
                </Typography>
                <Typography variant="h5">{statistics.totalCount}</Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent>
                <Typography color="textSecondary" gutterBottom>
                  Approved
                </Typography>
                <Typography variant="h5" color="success.main">
                  {statistics.approvedCount}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent>
                <Typography color="textSecondary" gutterBottom>
                  Total Amount
                </Typography>
                <Typography variant="h5">
                  ₹{statistics.totalAmount?.toFixed(2)}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent>
                <Typography color="textSecondary" gutterBottom>
                  Total Commission
                </Typography>
                <Typography variant="h5">
                  ₹{statistics.totalCommission?.toFixed(2)}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}

      {/* Filters */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} sm={6} md={3}>
            <LocalizationProvider dateAdapter={AdapterDateFns}>
              <DatePicker
                label="Start Date"
                value={startDate}
                onChange={(newValue) => setStartDate(newValue)}
                renderInput={(params) => <TextField {...params} fullWidth />}
              />
            </LocalizationProvider>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <LocalizationProvider dateAdapter={AdapterDateFns}>
              <DatePicker
                label="End Date"
                value={endDate}
                onChange={(newValue) => setEndDate(newValue)}
                renderInput={(params) => <TextField {...params} fullWidth />}
              />
            </LocalizationProvider>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <FormControl fullWidth>
              <InputLabel>Status</InputLabel>
              <Select
                value={status}
                onChange={(e) => setStatus(e.target.value)}
                label="Status"
              >
                <MenuItem value="">All</MenuItem>
                <MenuItem value="approved">Approved</MenuItem>
                <MenuItem value="pending">Pending</MenuItem>
                <MenuItem value="failed">Failed</MenuItem>
                <MenuItem value="rejected">Rejected</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Button
              variant="contained"
              color="primary"
              onClick={fetchSettlements}
              fullWidth
              disabled={loading}
            >
              Apply Filters
            </Button>
          </Grid>
        </Grid>
      </Paper>

      {/* DataGrid */}
      <Paper sx={{ height: 600, width: '100%' }}>
        <DataGrid
          rows={settlements}
          columns={columns}
          pageSize={pagination.limit}
          loading={loading}
          rowsPerPageOptions={[25, 50, 100]}
          pagination
          paginationMode="server"
          rowCount={pagination.totalRecords}
          page={pagination.currentPage - 1}
          onPageChange={handlePageChange}
          getRowId={(row) => row._id}
          disableSelectionOnClick
        />
      </Paper>

      {/* Details Dialog */}
      <Dialog
        open={detailsDialogOpen}
        onClose={() => setDetailsDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Settlement Details</DialogTitle>
        <DialogContent>
          {selectedSettlement && (
            <Grid container spacing={2} sx={{ mt: 1 }}>
              <Grid item xs={12} sm={6}>
                <Typography variant="subtitle2" color="textSecondary">
                  Settlement Reference
                </Typography>
                <Typography variant="body1">
                  {selectedSettlement.settlementRefNo}
                </Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="subtitle2" color="textSecondary">
                  YPP Reference
                </Typography>
                <Typography variant="body1">
                  {selectedSettlement.yppReferenceNumber || 'N/A'}
                </Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="subtitle2" color="textSecondary">
                  Settlement Amount
                </Typography>
                <Typography variant="body1">
                  ₹{selectedSettlement.settlementAmount?.toFixed(2)}
                </Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="subtitle2" color="textSecondary">
                  Commission
                </Typography>
                <Typography variant="body1">
                  ₹{selectedSettlement.commission?.toFixed(2)}
                </Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="subtitle2" color="textSecondary">
                  Registered Count
                </Typography>
                <Typography variant="body1">
                  {selectedSettlement.registeredCount}
                </Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="subtitle2" color="textSecondary">
                  Not Registered Count
                </Typography>
                <Typography variant="body1">
                  {selectedSettlement.notRegisteredCount}
                </Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="subtitle2" color="textSecondary">
                  Total Transaction Count
                </Typography>
                <Typography variant="body1">
                  {selectedSettlement.totalTransactionCount}
                </Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="subtitle2" color="textSecondary">
                  Status
                </Typography>
                <Chip
                  label={selectedSettlement.settlementStatus?.toUpperCase()}
                  color={getStatusColor(selectedSettlement.settlementStatus)}
                  size="small"
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                {/* [CASH-MODE] renamed: NEFT/IMPS, not UPI/Cash */}
                <Typography variant="subtitle2" color="textSecondary">
                  Settlement Mode
                </Typography>
                <Typography variant="body1">
                  {selectedSettlement.paymentMode}
                </Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="subtitle2" color="textSecondary">
                  Settlement Date
                </Typography>
                <Typography variant="body1">
                  {format(new Date(selectedSettlement.settlementDate), 'dd MMM yyyy, hh:mm a')}
                </Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="subtitle2" color="textSecondary">
                  Settlement RRN
                </Typography>
                <Typography variant="body1">
                  {selectedSettlement.settlementRrn || 'N/A'}
                </Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="subtitle2" color="textSecondary">
                  Agent Code
                </Typography>
                <Typography variant="body1">
                  {selectedSettlement.agentcode}
                </Typography>
              </Grid>
            </Grid>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDetailsDialogOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Message Dialog */}
      <Dialog open={openDialog} onClose={() => setOpenDialog(false)}>
        <DialogTitle>Message</DialogTitle>
        <DialogContent>
          <Typography>{dialogMessage}</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDialog(false)}>Close</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default VendorAcemoneySettlements;
